import React from "react";
import ReactECharts from "echarts-for-react";
import SpiChart from "./reporting/SpiChart";
import SmaChart from "./reporting/SMAChart";
import FaparChart from "./reporting/FaparChart";
import CountiesChart from "./home/CountiesChart";

const isMobile = () => typeof window !== "undefined" && window.innerWidth < 768;
const isTablet = () => typeof window !== "undefined" && window.innerWidth >= 768 && window.innerWidth < 1024;

const responsiveHeight = (mobile, tablet, desktop) => {
  if (isMobile()) return mobile;
  if (isTablet()) return tablet;
  return desktop;
};

const chartTextSize = (desktopSize) => (isMobile() ? Math.max(8, desktopSize - 2) : desktopSize);

/* ---------- CDI Donut — latest snapshot severity breakdown ---------- */
function CdiDonut({ data }) {
  if (!data || data.length === 0) return null;
  const latest = data[data.length - 1];

  const pieces = [
    { name: "No drought", value: latest["No drought"] || 0, color: "#4CAF50" },
    { name: "Watch",      value: latest["Watch"] || 0,      color: "#fff645" },
    { name: "Warning",    value: latest["Warning"] || 0,    color: "#FFA600" },
    { name: "Alert",      value: latest["Alert"] || 0,      color: "#E53935" },
  ].filter((p) => p.value > 0);

  const option = {
    tooltip: { trigger: "item", formatter: "{b}: {c}%" },
    legend: {
      bottom: 0,
      textStyle: { fontSize: 11 },
      itemWidth: 12,
      itemHeight: 10,
    },
    series: [
      {
        type: "pie",
        radius: ["40%", "70%"],
        center: ["50%", "45%"],
        label: { show: true, formatter: "{b}\n{c}%", fontSize: 10 },
        data: pieces.map((p) => ({
          name: p.name,
          value: Math.round(p.value * 10) / 10,
          itemStyle: { color: p.color },
        })),
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: responsiveHeight("190px", "215px", "230px"), width: "100%" }} />;
}

/* ---------- Drought Risk Gauge — single severity needle ---------- */
function DroughtGauge({ data }) {
  if (!data || data.length === 0) return null;
  const latest = data[data.length - 1];

  const watch   = latest["Watch"] || 0;
  const warning = latest["Warning"] || 0;
  const alert   = latest["Alert"] || 0;
  const affected = Math.round((watch + warning + alert) * 10) / 10;

  const option = {
    series: [
      {
        type: "gauge",
        startAngle: 180,
        endAngle: 0,
        min: 0,
        max: 100,
        splitNumber: 4,
        pointer: { length: "60%", width: 5 },
        axisLine: {
          lineStyle: {
            width: 20,
            color: [
              [0.25, "#4CAF50"],
              [0.50, "#FFC107"],
              [0.75, "#FF9800"],
              [1,    "#E53935"],
            ],
          },
        },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        detail: {
          formatter: "{value}%\naffected",
          fontSize: 16,
          fontWeight: 600,
          offsetCenter: [0, "20%"],
          color: "#333",
        },
        data: [{ value: affected }],
      },
    ],
  };

  const legendItems = [
    { color: "#4CAF50", label: "Normal (0–25%)" },
    { color: "#FFC107", label: "Low (25–50%)" },
    { color: "#FF9800", label: "Moderate (50–75%)" },
    { color: "#E53935", label: "Severe (75–100%)" },
  ];

  return (
    <div>
      <ReactECharts option={option} style={{ height: responsiveHeight("150px", "175px", "190px"), width: "100%" }} />
      <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: isMobile() ? "8px" : "12px", marginTop: -8 }}>
        {legendItems.map((item) => (
          <span key={item.label} style={{ display: "inline-flex", alignItems: "center", fontSize: isMobile() ? 10 : 11, color: "#555" }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color, display: "inline-block", marginRight: 4 }} />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------- Trend Sparkline — drought-affected area over time ---------- */
function TrendSparkline({ data }) {
  if (!data || data.length < 2) return null;

  const series = data.map((row) => ({
    date: row.date,
    affected:
      Math.round(
        ((row["Watch"] || 0) + (row["Warning"] || 0) + (row["Alert"] || 0)) * 10
      ) / 10,
  }));

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        const p = params[0];
        return `${p.name}<br/>Affected: <b>${p.value}%</b>`;
      },
    },
    grid: { left: "8%", right: "4%", top: "12%", bottom: "14%", containLabel: true },
    xAxis: {
      type: "category",
      data: series.map((d) => d.date),
      boundaryGap: false,
      axisLabel: { fontSize: chartTextSize(10), rotate: isMobile() ? 32 : (series.length > 6 ? 30 : 0), hideOverlap: true },
    },
    yAxis: {
      type: "value",
      name: "% affected",
      nameTextStyle: { fontSize: chartTextSize(10) },
      axisLabel: { fontSize: chartTextSize(10) },
      max: 100,
    },
    series: [
      {
        type: "line",
        data: series.map((d) => d.affected),
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 2, color: "#E53935" },
        itemStyle: { color: "#E53935" },
        areaStyle: {
          color: {
            type: "linear",
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(229,57,53,0.35)" },
              { offset: 1, color: "rgba(229,57,53,0.05)" },
            ],
          },
        },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: responsiveHeight("165px", "188px", "200px"), width: "100%" }} />;
}

/* ---------- helpers for place-level key decoding ---------- */
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function _decodeDekadKey(key) {
  // cdi_MMDD or anomaly_MMDD → "Mon DX"
  const m = key.match(/^(?:cdi|anomaly)_(\d{2})(\d{2})$/);
  if (m) {
    const mon = MONTH_NAMES[parseInt(m[1], 10) - 1] || m[1];
    const day = parseInt(m[2], 10);
    const dek = day === 1 ? "D1" : day === 11 ? "D2" : "D3";
    return `${mon} ${dek}`;
  }
  // spi_MMTT → "Mon"
  const s = key.match(/^spi_(\d{2})\d{2}$/);
  if (s) return MONTH_NAMES[parseInt(s[1], 10) - 1] || s[1];
  return key;
}

/* ---------- Place CDI Step Chart ---------- */
function PlaceCdiChart({ data }) {
  if (!data || data.length === 0) return null;

  const CDI_LABELS = { 0: "No drought", 1: "Watch", 2: "Warning", 3: "Alert" };

  // Detect encoding: if max > 5 assume 0/3/8/10, else 0-3
  const rawMax = Math.max(...data.map((d) => d.value));
  const normalize = (v) => {
    if (rawMax <= 5) return v;
    if (v >= 10) return 3;
    if (v >= 8) return 2;
    if (v >= 3) return 1;
    return 0;
  };

  const years = [...new Set(data.map((d) => d.year))];
  const multiYear = years.length > 1;
  const labels = data.map((d) => {
    const dk = _decodeDekadKey(d.key);
    return multiYear ? `${d.year} ${dk}` : dk;
  });
  const values = data.map((d) => normalize(d.value));

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        const p = params[0];
        return `${p.name}<br/><b>${CDI_LABELS[p.value] || p.value}</b>`;
      },
    },
    grid: { left: isMobile() ? "10%" : "14%", right: "4%", top: "8%", bottom: isMobile() ? "24%" : "20%", containLabel: true },
    xAxis: {
      type: "category",
      data: labels,
      axisLabel: { fontSize: chartTextSize(9), rotate: isMobile() ? 40 : (labels.length > 12 ? 45 : 0), hideOverlap: true, interval: "auto" },
    },
    yAxis: {
      type: "value",
      min: 0,
      max: 3,
      interval: 1,
      axisLabel: { formatter: (v) => CDI_LABELS[v] || "", fontSize: chartTextSize(10) },
    },
    visualMap: {
      show: false,
      pieces: [
        { lte: 0, color: "#4CAF50" },
        { gt: 0, lte: 1, color: "#955825" },
        { gt: 1, lte: 2, color: "#FFA600" },
        { gt: 2, color: "#E53935" },
      ],
    },
    series: [
      {
        type: "line",
        step: "middle",
        data: values,
        symbol: "circle",
        symbolSize: 6,
        lineStyle: { width: 2 },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: responsiveHeight("180px", "220px", "240px"), width: "100%" }} />;
}

/* ---------- Place SPI Line Chart ---------- */
function PlaceSpiChart({ data }) {
  if (!data || data.length === 0) return null;

  // Filter to timescale 03 only
  const filtered = data.filter((d) => d.key.endsWith("03"));
  if (filtered.length === 0) return null;

  const years = [...new Set(filtered.map((d) => d.year))];
  const multiYear = years.length > 1;
  const labels = filtered.map((d) => {
    const mon = MONTH_NAMES[parseInt(d.key.slice(4, 6), 10) - 1] || d.key;
    return multiYear ? `${d.year} ${mon}` : mon;
  });
  const values = filtered.map((d) => Math.round(d.value * 100) / 100);

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        const p = params[0];
        return `${p.name}<br/>SPI-3: <b>${p.value}</b>`;
      },
    },
    grid: { left: "8%", right: "4%", top: "10%", bottom: isMobile() ? "22%" : "18%", containLabel: true },
    xAxis: {
      type: "category",
      data: labels,
      axisLabel: { fontSize: chartTextSize(9), rotate: isMobile() ? 40 : (labels.length > 12 ? 45 : 0), hideOverlap: true, interval: "auto" },
    },
    yAxis: {
      type: "value",
      name: "SPI-3",
      nameTextStyle: { fontSize: chartTextSize(10) },
      axisLabel: { fontSize: chartTextSize(10) },
    },
    series: [
      {
        type: "line",
        data: values,
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        lineStyle: { width: 2, color: "#1976D2" },
        itemStyle: { color: "#1976D2" },
        markLine: {
          silent: true,
          symbol: "none",
          lineStyle: { type: "dashed", width: 1 },
          data: [
            { yAxis: 0, lineStyle: { color: "#888" }, label: { show: false } },
            { yAxis: -1, lineStyle: { color: "#FFA600" }, label: { formatter: "Moderate drought", fontSize: chartTextSize(9), position: "insideEndTop" } },
            { yAxis: -2, lineStyle: { color: "#E53935" }, label: { formatter: "Severe drought", fontSize: chartTextSize(9), position: "insideEndTop" } },
          ],
        },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: responsiveHeight("180px", "220px", "240px"), width: "100%" }} />;
}

/* ---------- Place FAPAR Anomaly Bar Chart ---------- */
function PlaceFaparChart({ data }) {
  if (!data || data.length === 0) return null;

  const years = [...new Set(data.map((d) => d.year))];
  const multiYear = years.length > 1;
  const labels = data.map((d) => {
    const dk = _decodeDekadKey(d.key);
    return multiYear ? `${d.year} ${dk}` : dk;
  });
  const values = data.map((d) => Math.round(d.value * 1000) / 1000);

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        const p = params[0];
        return `${p.name}<br/>FAPAR anomaly: <b>${p.value}</b>`;
      },
    },
    grid: { left: "8%", right: "4%", top: "10%", bottom: isMobile() ? "24%" : "20%", containLabel: true },
    xAxis: {
      type: "category",
      data: labels,
      axisLabel: { fontSize: chartTextSize(9), rotate: isMobile() ? 40 : (labels.length > 12 ? 45 : 0), hideOverlap: true, interval: "auto" },
    },
    yAxis: {
      type: "value",
      name: "Anomaly",
      nameTextStyle: { fontSize: chartTextSize(10) },
      axisLabel: { fontSize: chartTextSize(10) },
    },
    series: [
      {
        type: "bar",
        data: values.map((v) => ({
          value: v,
          itemStyle: { color: v >= 0 ? "#4CAF50" : "#E53935" },
        })),
        markLine: {
          silent: true,
          symbol: "none",
          lineStyle: { type: "solid", color: "#333", width: 1 },
          data: [{ yAxis: 0, label: { show: false } }],
        },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: responsiveHeight("180px", "220px", "240px"), width: "100%" }} />;
}

/* ---------- Rainfall vs Average Grouped Bar Chart ---------- */
function RainfallChart({ data }) {
  if (!data || data.length === 0) return null;

  const years = [...new Set(data.map((d) => d.year))];
  const multiYear = years.length > 1;
  const labels = data.map((d) => (multiYear ? `${d.year} ${d.month}` : d.month));
  const actual = data.map((d) => (d.actual != null ? Math.round(d.actual * 10) / 10 : null));
  const average = data.map((d) => (d.average != null ? Math.round(d.average * 10) / 10 : null));

  const option = {
    tooltip: { trigger: "axis" },
    legend: { data: ["Actual", "Average"], bottom: 0, type: isMobile() ? "scroll" : "plain", textStyle: { fontSize: chartTextSize(11) } },
    grid: { left: "8%", right: "4%", top: "10%", bottom: isMobile() ? "22%" : "18%", containLabel: true },
    xAxis: {
      type: "category",
      data: labels,
      axisLabel: { fontSize: chartTextSize(9), rotate: isMobile() ? 32 : (labels.length > 6 ? 30 : 0), hideOverlap: true, interval: "auto" },
    },
    yAxis: {
      type: "value",
      name: "mm",
      nameTextStyle: { fontSize: chartTextSize(10) },
      axisLabel: { fontSize: chartTextSize(10) },
    },
    series: [
      {
        name: "Actual",
        type: "bar",
        data: actual,
        itemStyle: { color: "#1976D2" },
        barGap: "10%",
      },
      {
        name: "Average",
        type: "bar",
        data: average,
        itemStyle: { color: "#BDBDBD" },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: responsiveHeight("180px", "220px", "240px"), width: "100%" }} />;
}

/* ---------- CDI Overview with Flags (wraps CountiesChart) ---------- */
function CdiOverviewWithFlags({ data }) {
  if (!data || (Array.isArray(data) && data.length === 0)) return null;
  // CountiesChart expects { cdi: { overview: [...] } }
  let wrapped;
  if (Array.isArray(data)) {
    // Legacy shape: plain array of overview rows
    wrapped = { cdi: { overview: data } };
  } else if (data.cdi) {
    // Already in the expected wrapped shape
    wrapped = data;
  } else if (Array.isArray(data.overview)) {
    // Orchestrator shape: { overview: [...], percentage_area: ... }
    wrapped = { cdi: data };
  } else {
    // Fallback: pass through as-is (defensive)
    wrapped = data;
  }
  return <CountiesChart data={wrapped} />;
}

/* ---------- Chart config registry ---------- */
/* ---------- UNWDC stacked bar (6-category Driest→Wettest) ---------- */
function UnwdcChart({ data, chartTitle }) {
  if (!data || !Array.isArray(data) || data.length === 0) return null;
  const sorted = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
  const categories = ["Driest", "Drier", "Dry", "Wet", "Wetter", "Wettest"];
  const colors = ["#8B0000", "#E53935", "#FFA600", "#4FC3F7", "#1976D2", "#0D47A1"];

  const option = {
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: { data: categories, bottom: 0, ...(isMobile() ? { type: "scroll", textStyle: { fontSize: 10 }, itemWidth: 12, itemHeight: 10 } : {}) },
    grid: { left: "15%", right: "15%", bottom: isMobile() ? "18%" : "10%", containLabel: true },
    xAxis: { type: "category", data: sorted.map((r) => r.date) },
    yAxis: { type: "value", max: 100, name: "% area" },
    series: categories.map((cat, i) => ({
      name: cat,
      type: "bar",
      stack: "total",
      barWidth: isMobile() ? 12 : 22,
      itemStyle: { color: colors[i] },
      data: sorted.map((r) => r[cat] ?? 0),
    })),
  };

  return <ReactECharts option={option} style={{ height: responsiveHeight(220, 280, 340), width: "100%" }} />;
}

/* ---------- Forecast stacked bar (4-category probability) ---------- */
function ForecastChart({ data, chartTitle }) {
  if (!data || !Array.isArray(data) || data.length === 0) return null;
  const sorted = [...data].sort((a, b) => {
    const da = a.forecast_period || a.date || "";
    const db = b.forecast_period || b.date || "";
    return da.localeCompare(db);
  });
  const categories = ["Below Normal", "Normal to Below", "Normal to Above", "Above Normal"];
  const colors = ["#E53935", "#FFA600", "#4FC3F7", "#1976D2"];

  const option = {
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: { data: categories, bottom: 0, ...(isMobile() ? { type: "scroll", textStyle: { fontSize: 10 }, itemWidth: 12, itemHeight: 10 } : {}) },
    grid: { left: "15%", right: "15%", bottom: isMobile() ? "18%" : "10%", containLabel: true },
    xAxis: { type: "category", data: sorted.map((r) => r.forecast_period || r.date || "?") },
    yAxis: { type: "value", max: 100, name: "% area" },
    series: categories.map((cat, i) => ({
      name: cat,
      type: "bar",
      stack: "total",
      barWidth: isMobile() ? 16 : 30,
      itemStyle: { color: colors[i] },
      data: sorted.map((r) => r[cat] ?? 0),
    })),
  };

  return <ReactECharts option={option} style={{ height: responsiveHeight(220, 280, 340), width: "100%" }} />;
}

const CHART_CONFIG = {
  cdi_summary: {
    Component: CdiDonut,
    dataKey: "cdi_summary",
    propName: "data",
    title: "Drought Severity Breakdown",
  },
  drought_gauge: {
    Component: DroughtGauge,
    dataKey: "cdi_summary",
    propName: "data",
    title: "Drought Risk Level",
  },
  drought_trend: {
    Component: TrendSparkline,
    dataKey: "cdi_summary",
    propName: "data",
    title: "Drought Trend",
  },
  cdi_overview: {
    Component: CdiOverviewWithFlags,
    dataKey: "cdi_overview",
    propName: "data",
    title: "CDI Regional Overview",
  },
  spi: {
    Component: SpiChart,
    dataKey: "spi_03_summary",
    propName: "data",
    title: "SPI-03 Time Series",
  },
  sma: {
    Component: SmaChart,
    dataKey: "sma_summary",
    propName: "data",
    title: "Soil Moisture Anomaly Time Series",
  },
  fapar: {
    Component: FaparChart,
    dataKey: "fapar_anomaly_summary",
    propName: "data",
    title: "FAPAR Anomaly Time Series",
  },
  /* ---- SPI multi-accumulation charts ---- */
  spi_01: {
    Component: SpiChart,
    dataKey: "spi_01_summary",
    propName: "data",
    title: "SPI-01 (1-month) Time Series",
  },
  spi_06: {
    Component: SpiChart,
    dataKey: "spi_06_summary",
    propName: "data",
    title: "SPI-06 (6-month) Time Series",
  },
  /* ---- ERA5 SPI charts ---- */
  spi_era5_01: {
    Component: SpiChart,
    dataKey: "spi_01_era5_summary",
    propName: "data",
    title: "ERA5 SPI-01 (1-month, reanalysis)",
  },
  spi_era5_03: {
    Component: SpiChart,
    dataKey: "spi_03_era5_summary",
    propName: "data",
    title: "ERA5 SPI-03 (3-month, reanalysis)",
  },
  spi_era5_06: {
    Component: SpiChart,
    dataKey: "spi_06_era5_summary",
    propName: "data",
    title: "ERA5 SPI-06 (6-month, reanalysis)",
  },
  /* ---- UNWDC charts ---- */
  unwdc_01: {
    Component: UnwdcChart,
    dataKey: "unwdc_01_summary",
    propName: "data",
    title: "UNWDC-01 (1-month Wet/Dry Classification)",
  },
  unwdc_03: {
    Component: UnwdcChart,
    dataKey: "unwdc_03_summary",
    propName: "data",
    title: "UNWDC-03 (3-month Wet/Dry Classification)",
  },
  /* ---- Forecast chart ---- */
  forecast: {
    Component: ForecastChart,
    dataKey: "forecast_summary",
    propName: "data",
    title: "Seasonal Rainfall Forecast",
  },
  /* ---- place-level charts ---- */
  place_cdi: {
    Component: PlaceCdiChart,
    dataKey: "cdi_series",
    propName: "data",
    title: "CDI Classification Timeline",
  },
  place_spi: {
    Component: PlaceSpiChart,
    dataKey: "spi_series",
    propName: "data",
    title: "SPI-3 Time Series",
  },
  place_fapar: {
    Component: PlaceFaparChart,
    dataKey: "fapar_series",
    propName: "data",
    title: "FAPAR Anomaly Timeline",
  },
  place_rainfall: {
    Component: RainfallChart,
    dataKey: "rainfall_vs_average",
    propName: "data",
    title: "Rainfall vs Long-term Average",
  },
};

export default function ChatInlineChart({ chartKey, chartData }) {
  const config = CHART_CONFIG[chartKey];
  if (!config) return null;

  const data = chartData?.[config.dataKey];
  if (
    !data ||
    (Array.isArray(data) && data.length === 0) ||
    (typeof data === "object" && !Array.isArray(data) && Object.keys(data).length === 0)
  ) {
    return null;
  }

  const { Component, propName, title } = config;

  return (
    <div style={{ margin: isMobile() ? "12px 0" : "16px 0", padding: isMobile() ? 8 : 12, background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", minWidth: 0, overflow: "hidden" }}>
      <p style={{ fontWeight: 600, marginBottom: 8, fontSize: isMobile() ? 12 : 13, color: "#555" }}>{title}</p>
      <div style={{ overflowX: "auto", minWidth: 0 }}>
        <Component {...{ [propName]: data }} compact chartTitle={title} />
      </div>
    </div>
  );
}
